import * as THREE from 'three';
import { toggle as toggleFullscreen } from '../core/Fullscreen';
import { promptInstall } from '../core/Install';
import { setVibration, vibrate } from '../core/Haptics';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { GameRenderer } from '../core/Renderer';
import { DASH_COOLDOWN, Player } from '../entities/Player';
import { AudioSystem } from '../systems/AudioSystem';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { Enemies, type Enemy } from '../systems/Enemies';
import { Environment } from '../systems/Environment';
import { FxShapes, Overlay, Particles } from '../systems/Fx';
import { Hazards } from '../systems/Hazards';
import { Pickups, type ItemKind } from '../systems/Pickups';
import { SHRINE_COLORS, Shrines, applyShrineReward, type ShrineKind, type ShrineRewardOut } from '../systems/Shrines';
import { Spells, type OwnedSpell } from '../systems/Spells';
import { gearItemIcon, passiveIcon, spellIcon } from '../ui/icons';
import { heroPortrait } from '../ui/portrait';
import { Ui, escapeHtml, formatTime, type SlotView, type UpgradeOption } from '../ui/Ui';
import { applyStatic, charText, enemyText, formatUpgrade, gearName, getLang, num, passiveText, percent, setLang, shopText, spellDesc, spellName, stageText, t, type Lang } from '../i18n';
import type { UiKey } from '../i18n/en.ts';
import { rgb } from '../utils/color';
import { createSeededRandom } from '../utils/random';
import { CHESTS, GEAR_BASES, RARITIES, activePerks, gearMods, type GearItem, type GearPerkId } from './gear';
import { RunIntegrityGuard, deepFreeze } from './anticheat';
import { triggerBlinkSignature } from './blink';
import { applyDailyMutators, getDailyTrial, mutatorById, type MutatorId } from './daily';
import { PACE_STEP, clearPace, loadPace, paceAt, recordPace } from './pace';
import type { CharacterDef, PassiveDef, PlayerMods, StageDef } from './content';
import { CHARACTERS, PASSIVES, SHOP, SPELLS, STAGES, enemyById, passiveById, spellById } from './content';
import { BASE_HP, BASE_MAGNET, BASE_SPEED, MAX_PASSIVES, MAX_SPELLS, SPELL_MAX_LEVEL, addMods, affordableUpgrade, emptyMods, goldDrop, mageMastery, mageMasteryMods, mainLevel, mainLevelGold, mainLevelMods, xpToNext } from './rules';
import { defaults, loadSave, writeSave, type SaveData, type Settings } from './Save';
import type { Progress } from './merge';
import { Cloud, type ActResult, type Action, type RunSettings, type RunStart } from '../net/Cloud';
import type { AuthMode } from '../ui/Ui';
import type { World } from './World';

type State = 'menu' | 'playing' | 'levelup' | 'chest' | 'paused' | 'results';

// What the results screen shows. The server's answer fills in gold, xp, loot and the progress they landed in;
// until then the numbers are unknown and the screen says the rewards are being saved.
interface RunResults {
  victory: boolean;
  cleared: boolean;
  beforeXp: number;
  beforeMastery: number;
  beforeCleared: number;
  gold: number | null;
  xp: number | null;
  loot: GearItem | null;
  progress: Progress | null;
}

const CAMERA_OFFSET = new THREE.Vector3(0, 25, 14.5);
// Vertical FOV, adjusted so the horizontal view stays within [MIN_HFOV, MAX_HFOV]:
// portrait phones widen it (sides are not cramped), wide landscape screens narrow it (the hero is not tiny).
const CAMERA_FOV = 48;
const MIN_HFOV = 34;
const MAX_HFOV = 78;
const BOSS_RED = '#ff4d6d';
const NO_MARKERS: Array<{ x: number; z: number; color: string; label: string }> = [];
const PLAY_ACTIONS = new Set(['play', 'start', 'retry', 'next', 'daily-trial']); // signed-in players only

export class Game implements World {
  // --- World ---
  time = 0;
  rng = createSeededRandom((Date.now() >>> 0) ^ 0x9e3779b9);
  readonly scene = new THREE.Scene();
  readonly player = new Player();
  readonly particles = new Particles();
  readonly shapes = new FxShapes();
  readonly overlay: Overlay;
  readonly audio = new AudioSystem();
  readonly enemies: Enemies;
  readonly pickups: Pickups;
  readonly shrines: Shrines;
  readonly hazards: Hazards;
  stage: StageDef = STAGES[0];
  stageIndex = 0;
  stormTier = 0;
  endless = false;
  enemySpeedMult = 1;
  spawnRateMult = 1;
  mods: PlayerMods = emptyMods();
  reducedFx = false;

  readonly camera = new THREE.PerspectiveCamera(CAMERA_FOV, 1, 0.5, 140);
  private readonly gfx: GameRenderer;
  private readonly input: InputController;
  private readonly env: Environment;
  private readonly spells: Spells;
  private readonly ui: Ui;
  private readonly cloud: Cloud;
  private restored: Promise<void> = Promise.resolve();
  private playAfterSignIn = false;
  private tgRun = 0; // bumped to cancel a Telegram sign-in in progress
  private readonly loop: Loop;
  private readonly debug: DebugTools | null = null;
  private readonly tuning: DebugTuning = { bloom: 0.38, exposure: 1.05, god: false, timeScale: 1 };
  private readonly guard = new RunIntegrityGuard();
  // Per run, so the end of the run waits for its own token even when it ends before /api/run/start answers.
  private runStart: Promise<RunStart | null> = Promise.resolve(null);
  private runBestiary: Record<string, number> = {};
  private nextBeat = 30; // the next 30 s of game time that sends a check-in
  private acting = false; // one purchase in flight: a double click cannot buy twice
  private results: RunResults | null = null;
  private save: SaveData;
  private state: State = 'menu';
  private character: CharacterDef = CHARACTERS[0];

  // run state
  private passives: Array<{ def: PassiveDef; level: number }> = [];
  private level = 1;
  private xp = 0;
  private kills = 0;
  private goldRun = 0;
  private pendingLevels = 0;
  private pendingChests = 0;
  private options: UpgradeOption[] = [];
  private rerolls = 0;
  private banishes = 0;
  private readonly banishedIds = new Set<string>();
  private lockedOptions: UpgradeOption[] | null = null;
  private healMult = 1;
  private volatileDeath = false;
  private dailyActive = false;
  private activeMutators: MutatorId[] = [];
  private masteryRank = 0;
  private armoryTab: 'equipment' | 'chests' | 'market' = 'equipment';
  private selectedItem = ''; // id of the item open in #modal-item
  private revealedItems: string[] = []; // ids of the items in the open chest reveal
  private revives = 0;
  private perks = new Set<GearPerkId>();
  private arcPulseTimer = 0;
  private aegisCd = 0;
  private blinkHasteTimer = 0;
  private shrineBuffTimer = 0;
  private shrineBuffMight = 0.35;
  private shrineBuffHaste = 0.25;
  private wasDailySeed = false;
  // This run's kills every PACE_STEP seconds, and the same for this device's best run here (null: none, or a Daily).
  private paceMarks: number[] = [];
  private paceRef: number[] | null = null;
  private combo = 0;
  private comboTimer = 0;
  private lastHitBy = '';
  private bossDefeatedAt = -1;
  private bossName = '';
  private bossMaxHp = 1;
  private hadBoss = false;

  // feel
  private hitstopLeft = 0;
  private trauma = 0;
  private shakeClock = 0;
  private readonly camPos = new THREE.Vector3();
  private readonly lookAt = new THREE.Vector3();
  private readonly move = { x: 0, y: 0 };
  private botMove: { x: number; y: number } | null = null;
  private readonly raycaster = new THREE.Raycaster();
  private readonly ndc = new THREE.Vector2();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private readonly mouseGround = new THREE.Vector3();
  private menuAngle = 0;
  private frame = 0;
  private pausedForScreenshot = false;
  private readonly markers: Array<{ x: number; z: number; color: string; label: string }> = [];
  private markerCount = 0;
  private labelLang = '';
  private readonly labels = { boss: '', elite: '', chest: '', shrine: '' };
  private readonly overlayHp = { x: 0, z: 0, ratio: 1 };
  private readonly markChest = (x: number, z: number): void => this.mark(x, z, '#ffd24d', this.labels.chest);
  private readonly markShrine = (x: number, z: number, color: string): void => this.mark(x, z, color, this.labels.shrine);
  private readonly tmpColor = new THREE.Color();

  constructor(private readonly canvas: HTMLCanvasElement) {
    deepFreeze([CHARACTERS, PASSIVES, SHOP, SPELLS, STAGES, CHESTS, GEAR_BASES, RARITIES]);
    this.save = loadSave();
    setLang(this.save.settings.lang);
    applyStatic();
    this.gfx = new GameRenderer(canvas, this.scene, this.camera);
    this.overlay = new Overlay(this.el<HTMLCanvasElement>('#overlay-canvas'));
    this.input = new InputController(this.el('#touch-zone'), this.el('#stick'), this.el('#knob'), this.el('#dash-button'));
    this.env = new Environment(this.scene);
    this.enemies = new Enemies(this);
    this.hazards = new Hazards(this);
    this.spells = new Spells(this);
    this.pickups = new Pickups(this, (v) => this.onGem(v), (k) => this.onItem(k));
    this.shrines = new Shrines(this, (kind, x, z, r) => this.onShrineComplete(kind, x, z, r));
    this.scene.add(this.env.group, this.player.group, this.enemies.root, this.spells.root, this.pickups.root, this.shrines.root, this.shapes.group, this.particles.points);
    this.ui = new Ui({
      onAction: (a) => this.onAction(a),
      onSelectCharacter: (id) => this.selectCharacter(id),
      onSelectStage: (i) => this.selectStage(i),
      onSelectStormTier: (tTier) => this.selectStormTier(tTier),
      onBuy: (id) => this.buy(id),
      onClaimBounty: (id) => this.claimEnemyBounty(id),
      onChest: (id, count) => this.buyChest(id, count),
      onBuyGear: (base, rarity) => this.buyGear(base, rarity),
      onItem: (itemId) => this.openItem(itemId),
      onSettings: (s) => this.applySettings(s),
      onLanguage: (l) => this.setLanguage(l),
      onPick: (i) => this.pick(i),
      onReroll: () => this.reroll(),
      onBanish: () => this.banish(),
      onLock: () => this.toggleLock(),
      onAuth: (mode, email, password, name) => void this.auth(mode, email, password, name),
      onSignOut: () => void this.cloud.signOut().then(() => {
        clearPace();
        Object.assign(this.save, defaults(), { settings: this.save.settings });
        this.applyProgress(this.save);
        this.ui.showAccount(null);
      }),
      onLeaderboardStage: (stage) => void this.loadLeaderboard(stage),
    });
    this.cloud = new Cloud(() => this.save, (p) => this.applyProgress(p), () => {
      this.ui.renderAccountButton(this.cloud.account);
      this.ui.renderDaily(this.cloud.daily);
    });
    if (import.meta.env.DEV) {
      this.debug = new DebugTools(this.tuning, () => this.applyTuning(), {
        'Skip +60s': () => { this.guard.markTainted('debug_tools'); this.time += 60; },
        'Level up': () => { this.guard.markTainted('debug_tools'); this.pendingLevels += 1; },
        'Spawn boss': () => { this.guard.markTainted('debug_tools'); this.time = this.stage.duration; },
      });
    }
    this.loop = new Loop((d, e) => this.update(d, e), () => this.render());
    window.addEventListener('keydown', this.onKey);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.applySettings(this.save.settings, false);
    this.applyTuning();
    this.toMenu();
    if (import.meta.env.DEV) this.installTestHooks();
    this.restored = this.cloud.restore();
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    window.removeEventListener('keydown', this.onKey);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.input.dispose();
    this.audio.dispose();
    this.debug?.dispose();
    this.env.dispose();
    this.shrines.dispose();
    this.overlay.dispose();
    this.gfx.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  // ======================= World callbacks =======================

  // iframes false: a hazard's damage-over-time tick, which neither grants i-frames nor triggers on-hit effects.
  hurtPlayer(amount: number, source: string, iframes = true): void {
    if (this.state !== 'playing' || this.tuning.god || this.bossDefeatedAt >= 0) return;
    const dmg = this.player.hurt(amount, this.mods.armor, iframes);
    if (dmg <= 0) return;
    this.lastHitBy = source;
    if (!iframes) {
      this.overlay.text(this.player.x, 2.4, this.player.z, num(-Math.round(dmg)), '#ffb36b', 14);
      return;
    }
    vibrate(25);
    this.ui.hurt();
    this.audio.hurt();
    this.shake(0.22);
    this.overlay.text(this.player.x, 2.4, this.player.z, num(-Math.round(dmg)), '#ff6b81', 17);
    if (this.perks.has('aegis_pulse') && this.aegisCd <= 0) {
      this.aegisCd = 8;
      this.player.invuln = Math.max(this.player.invuln, 1.5);
      this.shapes.ring(this.player.x, this.player.z, 0.4, 5.5, this.tmpColor.set('#ffc23d'), 0.5, 2.8);
      const waveDmg = 40 * (1 + this.mods.might);
      for (let i = 0, len = this.enemies.active.length; i < len; i += 1) {
        const e = this.enemies.active[i];
        if (!e.alive || e.def.behavior === 'static') continue;
        const dx = e.x - this.player.x;
        const dz = e.z - this.player.z;
        const d = Math.hypot(dx, dz) || 1;
        if (d < 5.5) this.enemies.applyDamage(e, waveDmg, dx / d, dz / d, 22);
      }
    }
  }

  onEnemyKilled(e: Enemy): void {
    const glow = this.tmpColor.copy(rgb(e.def.glow));
    if (e.def.behavior === 'static') {
      this.particles.burst(e.x, 1.2, e.z, 18, glow.multiplyScalar(3), 5, 0.5, 0.6, 1, -8);
      const roll = this.rng();
      const kind: ItemKind = roll < 0.32 ? 'food' : roll < 0.62 ? 'coin' : roll < 0.75 ? 'magnet' : roll < 0.87 ? 'bomb' : 'freeze';
      this.pickups.dropItem(kind, e.x, e.z);
      if (this.perks.has('sanctified_hearth')) {
        this.player.heal(15);
        this.goldRun += goldDrop(15, this.stageIndex, this.mods.greed);
      }
      this.audio.explode(0.15);
      return;
    }
    this.kills += 1;
    this.guard.recordEvent('kill', this.kills);
    this.runBestiary[e.def.id] = (this.runBestiary[e.def.id] ?? 0) + 1;
    this.audio.kill();
    if (e.xp > 0) this.pickups.dropGem(e.x, e.z, e.xp);
    const many = this.enemies.hostile > 150;
    this.particles.burst(e.x, 0.6 * e.scale, e.z, many ? 5 : 10, glow.multiplyScalar(1.8), 4.5, 0.4, 0.45, 0.8, -7);
    if (!many) this.particles.burst(e.x, 0.5, e.z, 4, this.tmpColor.copy(rgb(e.def.tint)).multiplyScalar(0.9), 3, 0.7, 0.5, 0.6, -4);
    if (this.rng() < 0.012 * (1 + this.mods.greed)) this.pickups.dropItem('coin', e.x + 0.4, e.z);
    if (this.rng() < 0.002) this.pickups.dropItem('food', e.x - 0.4, e.z);
    if (e.elite) {
      if (e.eliteAffix === 'hoarder') {
        for (let k = 0; k < 3; k += 1) {
          this.pickups.dropItem('coin', e.x + Math.cos(k * 2.1) * 0.9, e.z + Math.sin(k * 2.1) * 0.9);
        }
        this.pickups.dropItem('magnet', e.x, e.z - 0.8);
      }
      this.pickups.dropItem('chest', e.x, e.z);
      this.pickups.dropGem(e.x + 0.6, e.z + 0.6, 30 * this.stage.xpMult);
      this.goldRun += goldDrop(20, this.stageIndex, this.mods.greed);
      this.shapes.ring(e.x, e.z, 0.5, 5, rgb('#ffd24d'), 0.6, 3);
      this.hitstop(70);
      this.shake(0.4);
      this.audio.explode(0.8);
    }
    if (this.volatileDeath && !e.boss) {
      this.shapes.ring(e.x, e.z, 0.25, 2.5, this.tmpColor.set('#ff9a3c'), 0.32, 2.2);
      const blastDmg = e.maxHp * 0.2;
      for (let i = 0, len = this.enemies.active.length; i < len; i += 1) {
        const other = this.enemies.active[i];
        if (!other.alive || other === e || other.def.behavior === 'static') continue;
        const d = Math.hypot(other.x - e.x, other.z - e.z);
        if (d <= 2.5) this.enemies.applyDamage(other, blastDmg, 0, 0, 6);
      }
    }
    if (e.boss) this.bossDefeated(e);
  }

  private mark(x: number, z: number, color: string, label: string): void {
    const m = (this.markers[this.markerCount] ??= { x: 0, z: 0, color: '', label: '' });
    m.x = x;
    m.z = z;
    m.color = color;
    m.label = label;
    this.markerCount += 1;
  }

  shake(amount: number): void {
    if (this.save.settings.shake) this.trauma = Math.min(1, this.trauma + amount);
  }

  hitstop(ms: number): void {
    this.hitstopLeft = Math.max(this.hitstopLeft, ms / 1000);
  }

  banner(text: string, sub = '', color = '#ffffff'): void {
    this.ui.showBanner(text, sub, color);
  }

  // ======================= Loop =======================

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    const bufferH = this.gfx.resize();
    this.fitFov();
    this.particles.setScale(bufferH, this.camera.fov);
    this.updateSpawnRadius();
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }
    if (this.state === 'playing') {
      let scale = this.tuning.timeScale;
      if (this.hitstopLeft > 0) {
        this.hitstopLeft -= delta;
        scale *= 0.06;
      } else if (this.bossDefeatedAt >= 0) scale *= 0.35;
      const dt = delta * scale;
      this.guard.recordLiveStep(dt, this.tuning.timeScale, this.tuning.god);
      this.time += dt;
      this.simulate(dt);
      this.followCamera(delta);
      if (this.gfx.adapt(delta)) this.applyGraphics();
    } else if (this.state === 'menu') {
      const t = this.reducedFx ? 0 : elapsed;
      this.player.update(delta, t, 0, 0, false, 0, 0, 0);
      this.env.update(delta, 0, 0, this.particles, Math.random);
      this.particles.update(delta);
      this.shapes.update(delta, t);
      this.menuAngle += this.reducedFx ? 0 : delta * 0.08;
      this.camera.position.set(Math.sin(this.menuAngle) * 15, 9, Math.cos(this.menuAngle) * 15);
      this.camera.lookAt(0, 1.4, 0);
    }
    this.audio.update(delta);
    this.updateHud();
    const hp = this.overlayHp;
    hp.x = this.player.x;
    hp.z = this.player.z;
    hp.ratio = this.player.hp / this.player.maxHp;
    const menu = this.state === 'menu';
    this.overlay.draw(this.state === 'playing' ? delta : 0, this.camera, menu ? NO_MARKERS : this.markers, menu ? null : hp);
    this.publishDiagnostics();
  }

  private render(): void {
    this.gfx.render();
  }

  private paceKey(): string {
    return `${this.stage.id}:${this.stormTier}`; // a higher tier is a different race
  }

  private simulate(dt: number): void {
    while ((this.paceMarks.length + 1) * PACE_STEP <= this.time) this.paceMarks.push(this.kills);
    if (this.time >= this.nextBeat) this.sendBeat();
    const p = this.player;
    this.input.readMovement(this.move);
    if (this.move.x === 0 && this.move.y === 0) this.mouseMove();
    if (this.botMove) {
      this.move.x = this.botMove.x;
      this.move.y = this.botMove.y;
    }
    const forces = this.hazards.update(dt);
    if (this.blinkHasteTimer > 0) this.blinkHasteTimer = Math.max(0, this.blinkHasteTimer - dt);
    if (this.shrineBuffTimer > 0) {
      this.shrineBuffTimer = Math.max(0, this.shrineBuffTimer - dt);
      if (this.shrineBuffTimer === 0) this.recalc();
    }
    const speed = BASE_SPEED * (1 + this.mods.moveSpeed + (this.blinkHasteTimer > 0 ? 0.25 : 0)) * (1 - forces.slow);
    const dashed = p.update(dt, this.time, this.move.x, this.move.y, this.input.consumeDash(), speed, forces.pushX, forces.pushZ);
    if (dashed) {
      this.audio.dash();
      vibrate(10);
      const hostileList = this.enemies.active.filter((e) => e.alive && e.def.behavior !== 'static');
      const byIndex = new Map(hostileList.map((e) => [e.index, e]));
      const sig = triggerBlinkSignature(this.character.id, {
        x: p.x,
        z: p.z,
        might: this.mods.might,
        area: this.mods.area,
        masteryRank: this.masteryRank,
        enemies: hostileList.map((e) => ({ id: e.index, x: e.x, z: e.z, hp: e.hp })),
        hitEnemy: (hit) => {
          const target = byIndex.get(hit.enemyId);
          if (!target || !target.alive) return;
          if (hit.slowFactor > 0) this.enemies.slow(target, hit.slowFactor, hit.slowDur);
          const totalDmg = hit.damage + hit.burnDps * hit.burnDur * 0.5;
          this.enemies.applyDamage(target, totalDmg, hit.dirX, hit.dirZ, hit.knockback);
        },
        pullGemsRadius: (r) => this.pickups.magnetRadiusAt(p.x, p.z, r),
        grantHasteBurst: (dur) => {
          this.blinkHasteTimer = Math.max(this.blinkHasteTimer, dur);
        },
      });
      this.shapes.ring(p.x, p.z, 0.3, sig.radius * (1 + this.mods.area * 0.5), this.tmpColor.set(sig.color), 0.36, 2.4);
      if (this.masteryRank >= 3) p.dashCd *= 0.85;
      if (this.perks.has('stormstep')) {
        p.dashCd *= 0.8;
        this.shapes.ring(p.x, p.z, 0.4, 5, this.tmpColor.set('#5fc8ff'), 0.45, 2.6);
        const dmg = 35 * (1 + this.mods.might);
        for (let i = 0, len = this.enemies.active.length; i < len; i += 1) {
        const e = this.enemies.active[i];
          if (!e.alive || e.def.behavior === 'static') continue;
          const dx = e.x - p.x;
          const dz = e.z - p.z;
          const d = Math.hypot(dx, dz) || 1;
          if (d < 5) {
            this.enemies.slow(e, 0.5, 2.5);
            this.enemies.applyDamage(e, dmg, dx / d, dz / d, 14);
          }
        }
      }
    }
    if (p.dashing && !this.reducedFx) this.particles.emit(p.x, 1, p.z, 0, 0.5, 0, this.tmpColor.copy(rgb(this.character.trim)).multiplyScalar(2.2), 0.8, 0.35, 0, 0);
    for (const s of this.env.solids) {
      const dx = p.group.position.x - s.x;
      const dz = p.group.position.z - s.z;
      const rr = s.r + p.radius;
      const d2 = dx * dx + dz * dz;
      if (d2 < rr * rr) {
        const d = Math.sqrt(d2) || 0.01;
        p.group.position.x = s.x + (dx / d) * rr;
        p.group.position.z = s.z + (dz / d) * rr;
      }
    }
    if (this.mods.regen > 0) p.heal(this.mods.regen * dt);
    if (this.aegisCd > 0) this.aegisCd = Math.max(0, this.aegisCd - dt);
    if (this.perks.has('arc_pulse')) {
      this.arcPulseTimer -= dt;
      if (this.arcPulseTimer <= 0) {
        this.arcPulseTimer = 6;
        this.shapes.ring(p.x, p.z, 0.5, 6.5, this.tmpColor.set('#ffc23d'), 0.5, 2.8);
        const dmg = 45 * (1 + this.mods.might);
        for (let i = 0, len = this.enemies.active.length; i < len; i += 1) {
        const e = this.enemies.active[i];
          if (!e.alive || e.def.behavior === 'static') continue;
          const dx = e.x - p.x;
          const dz = e.z - p.z;
          const d = Math.hypot(dx, dz) || 1;
          if (d < 6.5) this.enemies.applyDamage(e, dmg, dx / d, dz / d, 18);
        }
      }
    }

    this.enemies.solids = this.env.solids;
    this.enemies.update(dt);
    this.spells.update(dt * (this.blinkHasteTimer > 0 ? 1.25 : 1));
    this.pickups.update(dt, BASE_MAGNET * (1 + this.mods.magnet));
    this.shrines.update(dt);
    this.env.update(dt, p.x, p.z, this.particles, Math.random);
    this.particles.update(dt);
    this.shapes.update(dt, this.time);

    this.comboTimer -= dt;
    if (this.comboTimer <= 0) this.combo = 0;
    const pressure = Math.min(1, this.enemies.hostile / 120);
    this.audio.setIntensity(this.enemies.boss ? 1 : 0.15 + pressure * 0.75);

    // Off-screen markers reuse their objects and labels every frame (no per-frame garbage: GC pauses stutter on phones).
    if (this.labelLang !== getLang()) {
      this.labelLang = getLang();
      this.labels.boss = t('marker.boss');
      this.labels.elite = t('marker.elite');
      this.labels.chest = t('marker.chest');
      this.labels.shrine = t('marker.shrine');
    }
    this.markerCount = 0;
    if (this.enemies.boss) this.mark(this.enemies.boss.x, this.enemies.boss.z, BOSS_RED, this.labels.boss);
    for (const e of this.enemies.active) if (e.alive && e.elite) this.mark(e.x, e.z, '#ffd24d', this.labels.elite);
    this.pickups.chests(this.markChest);
    this.shrines.activeShrines(this.markShrine);
    this.markers.length = this.markerCount;
    if (this.enemies.boss) {
      this.bossName = enemyText(this.enemies.boss.def.id).name;
      this.bossMaxHp = this.enemies.boss.maxHp;
    }
    const bossUp = this.enemies.boss !== null;
    if (bossUp && !this.hadBoss) vibrate(120);
    this.hadBoss = bossUp;

    if (p.hp > p.maxHp + 0.01) {
      p.hp = p.maxHp;
      this.guard.markTainted('memory_tamper');
    }
    if (p.hp <= 0) {
      if (this.revives > 0) {
        this.revives -= 1;
        p.hp = p.maxHp * 0.5;
        p.invuln = 3;
        this.banner(t('banner.phoenix'), t('banner.phoenixSub'), '#ffb347');
        this.shapes.ring(p.x, p.z, 0.5, 8, this.tmpColor.set('#ffb347'), 0.8, 3);
        const buf: Enemy[] = [];
        for (const e of this.enemies.active) if (e.alive && !e.boss) buf.push(e);
        for (const e of buf) {
          const dx = e.x - p.x;
          const dz = e.z - p.z;
          const d = Math.hypot(dx, dz) || 1;
          if (d < 8) this.enemies.applyDamage(e, e.maxHp * 0.5, dx / d, dz / d, 30);
        }
        this.audio.chest();
      } else {
        vibrate(200);
        this.endRun(false);
        return;
      }
    }
    if (this.bossDefeatedAt >= 0 && this.time - this.bossDefeatedAt > 1.2) {
      if (this.endless) {
        this.bossDefeatedAt = -1;
        this.goldRun += goldDrop(75, this.stageIndex, this.mods.greed);
        this.pickups.dropItem('chest', this.player.x, this.player.z);
      } else {
        this.endRun(true);
        return;
      }
    }
    if (this.pendingChests > 0) this.openChest();
    else if (this.pendingLevels > 0) this.openLevelUp();
  }

  // Walk toward the pointer, projected onto the ground so diagonals match the tilted camera.
  // Speed eases to zero within ~1.5 units of the hero, so parking the cursor on yourself stops you.
  private mouseMove(): void {
    const target = this.input.mouseTarget();
    if (!target) return;
    const rect = this.canvas.getBoundingClientRect();
    this.ndc.set(((target.x - rect.left) / rect.width) * 2 - 1, -((target.y - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    if (!this.raycaster.ray.intersectPlane(this.groundPlane, this.mouseGround)) return;
    const dx = this.mouseGround.x - this.player.x;
    const dz = this.mouseGround.z - this.player.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.35) return;
    const mag = Math.min(1, (dist - 0.35) / 1.2);
    this.move.x = (dx / dist) * mag;
    this.move.y = (dz / dist) * mag;
  }

  private followCamera(dt: number): void {
    const p = this.player.group.position;
    const k = 1 - Math.exp(-dt / 0.09);
    this.camPos.lerp(this.lookAt.set(p.x, 0, p.z).add(CAMERA_OFFSET), k);
    this.camera.position.copy(this.camPos);
    this.camera.lookAt(this.camPos.x, 0, this.camPos.z - CAMERA_OFFSET.z - 1);
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
    if (this.trauma > 0) {
      this.shakeClock += dt * 30;
      const s = this.trauma * this.trauma;
      const n = (seed: number) => {
        const x = Math.sin(this.shakeClock * 1.7 + seed * 78.233) * 43758.5453;
        return (x - Math.floor(x)) * 2 - 1;
      };
      this.camera.position.x += 0.6 * s * n(1);
      this.camera.position.y += 0.4 * s * n(2);
      this.camera.position.z += 0.6 * s * n(3);
    }
  }

  private fitFov(): void {
    const forWidth = (hfov: number) => 2 * THREE.MathUtils.radToDeg(Math.atan(Math.tan(THREE.MathUtils.degToRad(hfov / 2)) / this.camera.aspect));
    const fov = Math.min(Math.max(CAMERA_FOV, forWidth(MIN_HFOV)), forWidth(MAX_HFOV));
    if (fov === this.camera.fov) return;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
  }

  private updateSpawnRadius(): void {
    const tan = Math.tan(THREE.MathUtils.degToRad(this.camera.fov / 2));
    const dist = CAMERA_OFFSET.length();
    const halfH = dist * tan * 1.35;
    const halfW = dist * tan * this.camera.aspect;
    this.enemies.spawnRadius = Math.max(18, Math.hypot(halfW, halfH) + 2);
  }

  // ======================= Run flow =======================

  private toMenu(): void {
    this.state = 'menu';
    this.dailyActive = false;
    if (this.wasDailySeed) {
      // The armory draws from this rng too: leaving it on the day's seed would make chest pulls predictable.
      this.wasDailySeed = false;
      this.rng = createSeededRandom((Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0);
    }
    this.activeMutators = [];
    this.ui.setHud(false);
    this.ui.hideLevelUp();
    this.ui.hidePause();
    this.ui.hideResults();
    this.loadStage(this.save.stage);
    this.player.setCharacter(this.currentCharacter());
    this.player.reset(BASE_HP);
    this.ui.renderTitle(this.save);
    this.ui.show('title');
    this.audio.playMusic(this.stage.music);
    this.audio.setIntensity(0);
  }

  private loadStage(index: number): void {
    this.stageIndex = index;
    this.stage = STAGES[index];
    this.env.build(this.stage, index, this.gfx.shadowSize);
    const ids = new Set<string>(['brazier', this.stage.elite, this.stage.boss, this.stage.swarmer, this.stage.flyer]);
    for (const [id] of this.stage.roster) {
      ids.add(id);
      const child = enemyById(id).child;
      if (child) ids.add(child);
    }
    this.enemies.prepareStage([...ids]);
    this.gfx.warm(); // every enemy model of the stage now exists, invisible: compile them all before the run needs them
    this.spells.reset();
    this.pickups.clear();
    this.shrines.reset();
    this.hazards.reset(null);
    this.particles.clear();
    this.shapes.clear();
    this.overlay.clear();
  }

  private currentCharacter(): CharacterDef {
    const c = CHARACTERS.find((ch) => ch.id === this.save.character && this.save.cleared >= ch.unlockStage);
    return c ?? CHARACTERS[0];
  }

  // The run's settings, as the server signs them into its token.
  private runSettings(daily: boolean): RunSettings {
    if (daily) {
      const spec = getDailyTrial();
      return { stage: STAGES[spec.stageIndex].id, tier: spec.stormTier, character: spec.characterId, daily: true };
    }
    const character = this.currentCharacter();
    return { stage: STAGES[this.save.stage].id, tier: this.save.stormTier ?? 0, character: character.id, daily: false };
  }

  // Ask for the run's token before the run starts, so the mods it plays with are the server's progress and not this
  // device's cache. A failed start still plays, it just cannot be submitted.
  private async beginRun(daily: boolean): Promise<void> {
    const settings = this.runSettings(daily);
    const start = this.cloud.startRun(settings);
    await this.waitFor(start, 4000);
    this.startRun(daily, start, settings); // the token signed these: a pick changed during the wait must not count
  }

  private async waitFor<T>(promise: Promise<T>, ms: number): Promise<T | null> {
    return Promise.race([promise, new Promise<null>((resolve) => window.setTimeout(() => resolve(null), ms))]);
  }

  private startRun(daily = false, start?: Promise<RunStart | null>, settings?: RunSettings): void {
    this.ui.hideResults();
    this.ui.show(null);
    this.dailyActive = daily;
    this.activeMutators = [];
    this.enemySpeedMult = 1;
    this.spawnRateMult = 1;
    this.healMult = 1;
    this.volatileDeath = false;
    this.banishedIds.clear();
    this.lockedOptions = null;
    if (daily) {
      const spec = getDailyTrial();
      this.wasDailySeed = true;
      this.rng = createSeededRandom(spec.seed);
      this.loadStage(spec.stageIndex);
      this.character = CHARACTERS.find((c) => c.id === spec.characterId) ?? CHARACTERS[0];
      this.stormTier = spec.stormTier;
      this.activeMutators = [...spec.mutators];
      for (const mId of this.activeMutators) {
        const mDef = mutatorById(mId);
        this.enemySpeedMult *= mDef.enemySpeedMult;
        this.spawnRateMult *= mDef.spawnRateMult;
        this.healMult *= mDef.healMult;
        if (mDef.volatileDeath) this.volatileDeath = true;
      }
    } else {
      if (this.wasDailySeed) {
        this.wasDailySeed = false;
        this.rng = createSeededRandom((Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0);
      }
      const index = settings ? STAGES.findIndex((s) => s.id === settings.stage) : -1;
      this.loadStage(index >= 0 ? index : this.save.stage);
      this.character = (settings && CHARACTERS.find((c) => c.id === settings.character)) || this.currentCharacter();
      this.stormTier = settings?.tier ?? this.save.stormTier ?? 0;
    }
    this.hazards.reset(this.stage.hazard);
    this.masteryRank = mageMastery(this.save.mastery?.[this.character.id] ?? 0).rank;
    this.passives = [];
    this.level = 1;
    this.xp = 0;
    this.kills = 0;
    this.goldRun = 0;
    this.pendingLevels = 0;
    this.pendingChests = 0;
    this.combo = 0;
    this.time = 0;
    this.paceMarks = [];
    this.paceRef = daily ? null : (loadPace(this.paceKey())?.marks ?? null);
    this.guard.reset(this.stage.id);
    this.runStart = start ?? this.cloud.startRun(this.runSettings(daily));
    this.runBestiary = {};
    this.nextBeat = 30;
    this.endless = false;
    this.bossDefeatedAt = -1;
    this.hadBoss = false;
    this.trauma = 0;
    this.lastHitBy = '';
    this.mods = this.computeMods();
    this.perks = new Set(activePerks(this.save.gear));
    this.arcPulseTimer = 6;
    this.aegisCd = 0;
    this.blinkHasteTimer = 0;
    this.shrineBuffTimer = 0;
    this.shrineBuffMight = 0.35;
    this.shrineBuffHaste = 0.25;
    this.rerolls = this.mods.reroll + (this.perks.has('scholar_crown') ? 1 : 0);
    this.banishes = 1 + this.mods.reroll + (this.perks.has('scholar_crown') ? 1 : 0);
    this.revives = this.mods.revive;
    this.player.setCharacter(this.character);
    this.player.reset(Math.max(25, BASE_HP + this.mods.maxHp), this.healMult);
    const sigSpell = spellById(this.character.spell);
    this.spells.add(sigSpell, this.mods);
    if (this.masteryRank >= 5) {
      const evoPassive = passiveById(sigSpell.evolution.passive);
      if (evoPassive) {
        this.passives.push({ def: evoPassive, level: 1 });
        this.recalc();
      }
    }
    this.camPos.copy(this.player.group.position).add(CAMERA_OFFSET);
    this.input.clear();
    this.state = 'playing';
    this.ui.setHud(true);
    this.refreshSlots();
    this.audio.playMusic(this.stage.music);
    const subBanner = this.dailyActive && this.activeMutators.length >= 2
      ? t('daily.bannerSub', { m1: t(`mutator.${this.activeMutators[0]}.name` as UiKey), m2: t(`mutator.${this.activeMutators[1]}.name` as UiKey) })
      : stageText(this.stage.id).subtitle;
    this.banner(stageText(this.stage.id).name, subBanner, this.stage.visuals.accent);
  }

  permanentMods(): PlayerMods {
    const mods = emptyMods();
    for (const item of SHOP) addMods(mods, item.perRank, this.save.shop[item.id] ?? 0);
    addMods(mods, gearMods(this.save.gear));
    const activeChar = this.dailyActive ? this.character : this.currentCharacter();
    addMods(mods, activeChar.mods);
    addMods(mods, mainLevelMods(mainLevel(this.save.mainXp).level));
    const charId = this.dailyActive ? this.character.id : this.currentCharacter().id;
    addMods(mods, mageMasteryMods(mageMastery(this.save.mastery?.[charId] ?? 0).rank));
    return mods;
  }

  private computeMods(): PlayerMods {
    const mods = this.permanentMods();
    for (const p of this.passives) addMods(mods, p.def.perLevel, p.level);
    if (this.shrineBuffTimer > 0) addMods(mods, { might: this.shrineBuffMight, haste: this.shrineBuffHaste });
    if (this.activeMutators.length > 0) applyDailyMutators(mods, this.activeMutators);
    return mods;
  }

  private onShrineComplete(kind: ShrineKind, x: number, z: number, rawReward: ShrineRewardOut): void {
    const reward = kind === 'anvil' ? applyShrineReward('anvil', this.player.hp, this.goldRun) : rawReward;
    if (reward.spendGold && reward.spendGold > 0) {
      this.goldRun = Math.max(0, this.goldRun - reward.spendGold);
    }
    if (reward.upgradeRandomSpell) {
      const unmaxed = this.spells.owned.filter((s) => !s.evolved && s.level < SPELL_MAX_LEVEL);
      if (unmaxed.length > 0) {
        const chosen = unmaxed[Math.floor(this.rng() * unmaxed.length)];
        chosen.level += 1;
        this.spells.refresh(this.mods);
        this.refreshSlots();
      } else {
        this.shrineBuffTimer = Math.max(this.shrineBuffTimer, 25);
        this.shrineBuffMight = 0.25;
        this.shrineBuffHaste = 0.15;
        this.recalc();
      }
    }
    const color = SHRINE_COLORS[kind];
    this.shapes.ring(x, z, 0.5, 7.5, this.tmpColor.set(color), 0.65, 3);
    this.audio.levelUp();
    vibrate(40);
    if (reward.heal > 0) this.player.heal(reward.heal);
    if (reward.buffDuration > 0) {
      this.shrineBuffTimer = Math.max(this.shrineBuffTimer, reward.buffDuration);
      this.shrineBuffMight = reward.buffMight || 0.35;
      this.shrineBuffHaste = reward.buffHaste || 0.25;
      this.recalc();
    }
    if (reward.selfDamage > 0) {
      this.player.hp = Math.max(1, this.player.hp - reward.selfDamage);
    }
    if (reward.spawnElites > 0) {
      const def = enemyById(this.stage.elite);
      for (let i = 0; i < reward.spawnElites; i += 1) {
        const a = (i / reward.spawnElites) * Math.PI * 2;
        this.enemies.spawn(def, x + Math.cos(a) * 5, z + Math.sin(a) * 5, true);
      }
    }
    if (reward.dropChest) this.pickups.dropItem('chest', x, z);
    if (reward.gold > 0) this.goldRun += goldDrop(reward.gold, this.stageIndex, this.mods.greed);
    this.guard.recordEvent('shrine', reward.gold);
    if (reward.vacuumGems) this.pickups.magnetAll();
    this.banner(t(`shrine.${kind}.title` as UiKey), t(`shrine.${kind}.sub` as UiKey), color);
  }

  private recalc(): void {
    const before = this.player.maxHp;
    this.mods = this.computeMods();
    this.spells.refresh(this.mods);
    const maxHp = BASE_HP + this.mods.maxHp;
    this.player.maxHp = maxHp;
    if (maxHp > before) this.player.hp += maxHp - before;
    this.refreshSlots();
  }

  private bossDefeated(e: Enemy): void {
    this.bossDefeatedAt = this.time;
    this.guard.recordEvent('boss', Math.round(this.time));
    this.hitstop(220);
    this.shake(1);
    this.ui.flash(0.9, 400);
    const c = this.tmpColor.set(e.def.glow);
    this.shapes.ring(e.x, e.z, 1, 14, c, 1.2, 3);
    this.shapes.disc(e.x, e.z, 1, 9, c, 0.8, 1.5);
    this.particles.burst(e.x, 2, e.z, 120, c.clone().multiplyScalar(3), 14, 0.9, 1.4, 1.2, -6);
    this.audio.explode(1);
    this.banner(t('banner.victory'), t('banner.fallen', { name: enemyText(e.def.id).name }), '#ffd24d');
    for (const other of [...this.enemies.active]) if (other.alive && other.def.behavior !== 'static') this.enemies.kill(other);
    this.pickups.magnetAll();
  }

  // The run is over: the screen opens at once and the server's own answer fills in what it earned. Nothing is
  // credited here: gold, xp, mastery, unlocks, the codex and loot all arrive from /api/score.
  private endRun(victory: boolean): void {
    this.state = 'results';
    this.input.clear();
    this.guard.recordEvent('end', this.kills);
    const cleared = victory || this.endless;
    if (!this.dailyActive) recordPace(this.paceKey(), { victory: cleared, kills: this.kills, time: this.time, marks: this.paceMarks });
    if (victory) this.audio.victory();
    else this.audio.defeat();
    this.audio.setIntensity(0);
    this.results = {
      victory,
      cleared,
      beforeXp: this.save.mainXp,
      beforeMastery: this.save.mastery[this.character.id] ?? 0,
      beforeCleared: this.save.cleared,
      gold: null,
      xp: null,
      loot: null,
      progress: null,
    };
    this.renderResults(t('results.syncing'));
    void this.finishRun();
  }

  private renderResults(note: string): void {
    const r = this.results;
    if (!r) return;
    const progress = r.progress;
    const beforeLevel = mainLevel(r.beforeXp).level;
    const afterLevel = progress ? mainLevel(progress.mainXp).level : beforeLevel;
    const beforeMastery = mageMastery(r.beforeMastery).rank;
    const afterMastery = progress ? mageMastery(progress.mastery[this.character.id] ?? 0).rank : beforeMastery;
    let levelGold = 0;
    for (let level = beforeLevel + 1; level <= afterLevel; level += 1) levelGold += mainLevelGold(level);
    const bonus = mainLevelMods(afterLevel);
    const masteryBonus = mageMasteryMods(afterMastery);
    const unlocked = progress && progress.cleared > r.beforeCleared ? CHARACTERS.find((c) => c.unlockStage === progress.cleared) : undefined;
    const sub = r.victory
      ? `${t('results.cleared', { stage: stageText(this.stage.id).name })}${unlocked ? ` · ${t('results.joins', { name: charText(unlocked.id).name, title: charText(unlocked.id).title })}` : ''}`
      : t('results.slain', { killer: this.killerName(this.lastHitBy), time: formatTime(this.time) });
    const rows = [...this.spells.owned].sort((a, b) => b.damage - a.damage);
    const top = Math.max(1, rows[0]?.damage ?? 1);
    const loot = r.loot;
    const upgrade = progress ? affordableUpgrade(progress.shop, progress.gold) : null;
    const html = `
      <div class="summary">
        <div><b>${formatTime(this.time)}</b><span>${t('results.survived')}</span></div>
        <div><b>${num(this.level)}</b><span>${t('results.level')}</span></div>
        <div><b>${num(this.kills)}</b><span>${t('results.kills')}</span></div>
        <div><b class="gold">${r.gold === null ? '…' : `+${num(r.gold)}`}</b><span>${t('results.gold')}</span></div>
        <div><b class="xp">${r.xp === null ? '…' : `+${num(r.xp)}`}</b><span>${t('results.xp')}</span></div>
      </div>
      ${afterLevel > beforeLevel ? `<p class="main-levelup">${escapeHtml(t('results.mainLevelUp', { n: num(afterLevel), gold: num(levelGold), dmg: num(Math.round(bonus.might * 100)), hp: num(bonus.maxHp) }))}</p>` : ''}
      ${afterMastery > beforeMastery ? `<p class="main-levelup">${escapeHtml(t('results.masteryUp', { n: num(afterMastery), dmg: num(Math.round(masteryBonus.might * 100)), hp: num(masteryBonus.maxHp) }))}</p>` : ''}
      ${loot ? `<p class="main-levelup boss-loot"><span class="ticon" style="--c:${RARITIES[loot.rarity].color}">${gearItemIcon(loot.base)}</span> ${escapeHtml(t('results.bossLoot', { rarity: t(`rarity.${RARITIES[loot.rarity].id}` as UiKey), name: gearName(loot.base) }))}</p>` : ''}
      ${upgrade ? `<p class="shop-tip">${escapeHtml(t('results.canBuy', { name: shopText(upgrade.id).name, desc: shopText(upgrade.id).desc }))}</p>` : ''}
      ${this.damageTable(rows, top)}`;
    const actions: Array<[string, string, boolean]> = [];
    if (r.victory && !this.endless) actions.push(['endless', t('results.endless'), false]);
    const next = r.victory && !this.dailyActive && this.stageIndex + 1 < STAGES.length;
    if (next) actions.push(['next', t('results.next'), true]);
    actions.push(['retry', r.victory ? t('results.again') : t('results.retry'), !next]);
    actions.push(['shop', t('menu.shop'), false], ['menu', t('results.menu'), false]);
    this.ui.showResults(r.victory ? t('results.victory') : t('results.defeated'), sub, html, actions, r.victory);
    this.ui.setResultsNote(note);
  }

  // One check-in per 30 s of game time, fired and forgotten: it must never pause, slow or fail a run.
  private sendBeat(): void {
    this.nextBeat = (Math.floor(this.time / 30) + 1) * 30;
    const t = this.time;
    const kills = this.kills;
    const level = this.level;
    const gold = this.goldRun;
    void this.runStart.then((started) => {
      if (started) void this.cloud.beat(started.token, t, kills, level, gold);
    });
  }

  private async finishRun(): Promise<void> {
    const r = this.results;
    if (!r) return;
    if (!this.cloud.account || this.guard.tainted) {
      this.ui.setResultsNote(t(this.guard.tainted ? 'results.tainted' : 'results.signInToSubmit'));
      return;
    }
    const started = await this.waitFor(this.runStart, 4000);
    if (!started) {
      this.ui.setResultsNote(t('results.signInToSubmit'));
      return;
    }
    const result = await this.cloud.submitScore({
      stage: this.stage.id,
      kills: this.kills,
      time: Math.min(this.time, 3600),
      victory: r.cleared,
      level: this.level,
      tier: this.stormTier,
      character: this.character.id,
      daily: this.dailyActive,
      goldRun: this.goldRun,
      greed: this.mods.greed,
      bestiary: this.runBestiary,
      evolved: this.spells.owned.filter((s) => s.evolved).map((s) => s.def.id),
      token: started.token,
    });
    // The player may have left the screen while the answer was on its way.
    if (this.results !== r || this.state !== 'results' || !result?.progress) {
      if (this.results === r && this.state === 'results') this.ui.setResultsNote(t('results.notSaved'));
      return;
    }
    r.gold = Math.max(0, result.gold ?? 0);
    r.xp = Math.max(0, result.progress.mainXp - r.beforeXp);
    r.loot = result.loot ?? null;
    r.progress = result.progress;
    const note = this.dailyActive
      ? `${t(result.improved ? 'results.dailyBest' : 'results.dailyRank', { n: result.rank })} · 🔥 ${t('daily.streak', { n: result.streak ?? 1 })}`
      : t(result.improved ? 'results.newBest' : 'results.submitted', { n: result.rank });
    this.renderResults(note);
  }

  // Enemy ids and killer.* keys come from World.hurtPlayer.
  private killerName(source: string): string {
    if (!source) return t('killer.horde');
    return source.startsWith('killer.') ? t(source as UiKey) : enemyText(source).name;
  }

  private damageTable(rows: OwnedSpell[], top: number): string {
    const minutes = Math.max(1 / 60, this.time / 60);
    return `<table class="stats-table"><thead><tr><th>${t('table.spell')}</th><th>${t('table.level')}</th><th class="num">${t('table.damage')}</th><th class="num">${t('table.dps')}</th><th style="width:30%"></th></tr></thead><tbody>
      ${rows
        .map((s) => {
          return `<tr><td><span class="ticon" style="--c:${s.color.getStyle()}">${spellIcon(s.def.id, s.def.icon)}</span> ${escapeHtml(spellName(s.def, s.evolved))}</td><td>${s.evolved ? t('table.evo') : num(s.level)}</td>
            <td class="num">${num(Math.round(s.damage))}</td><td class="num">${num(Math.round(s.damage / (minutes * 60)))}</td>
            <td><div class="bar" style="--c:${s.def.color};width:${(s.damage / top) * 100}%"></div></td></tr>`;
        })
        .join('')}
    </tbody></table>`;
  }

  // ======================= Level ups & chests =======================

  private onGem(value: number): void {
    if (this.state !== 'playing' && this.state !== 'levelup' && this.state !== 'chest') return;
    this.xp += value * (1 + this.mods.growth);
    this.combo += 1;
    this.comboTimer = 0.6;
    this.audio.gem(this.combo);
    while (this.xp >= xpToNext(this.level)) {
      this.xp -= xpToNext(this.level);
      this.level += 1;
      this.pendingLevels += 1;
      this.guard.recordEvent('level', this.level);
    }
  }

  private onItem(kind: ItemKind): void {
    const p = this.player;
    switch (kind) {
      case 'coin':
        this.goldRun += goldDrop(10, this.stageIndex, this.mods.greed);
        if (this.perks.has('alchemist_loop')) {
          p.heal(4);
          this.onGem(5);
        }
        this.overlay.text(p.x, 2.2, p.z, t('float.gold'), '#ffd24d', 14);
        this.audio.pickup();
        break;
      case 'food':
        p.heal(30);
        this.overlay.text(p.x, 2.2, p.z, num(Math.round(30 * this.healMult), { signDisplay: 'always' }), '#7dff9a', 18);
        this.audio.pickup();
        break;
      case 'magnet':
        this.pickups.magnetAll();
        this.ui.flash(0.25, 200);
        this.audio.pickup();
        break;
      case 'freeze':
        for (const e of this.enemies.active) if (e.alive) this.enemies.slow(e, 1, 5);
        this.ui.flash(0.35, 300);
        this.banner(t('banner.timeStop'), '', '#9ff3ff');
        this.audio.cast('nova');
        break;
      case 'bomb': {
        const victims = this.enemies.active.filter((e) => e.alive && e.def.behavior !== 'static' && Math.hypot(e.x - p.x, e.z - p.z) < 20);
        for (const e of victims) this.enemies.applyDamage(e, e.boss ? e.maxHp * 0.03 : e.elite ? e.maxHp * 0.15 : e.hp + 1, 0, 0, 0);
        this.ui.flash(0.8, 260);
        this.shake(0.6);
        this.shapes.ring(p.x, p.z, 1, 18, this.tmpColor.set('#ff9a3c'), 0.6, 3);
        this.audio.explode(1);
        break;
      }
      case 'chest':
        this.pendingChests += 1;
        break;
    }
  }

  private openLevelUp(): void {
    this.state = 'levelup';
    this.input.clear();
    if (this.lockedOptions && this.lockedOptions.length > 0) {
      this.options = this.rollOptions(this.lockedOptions);
      this.lockedOptions = null;
    } else {
      this.options = this.rollOptions();
    }
    this.audio.levelUp();
    vibrate(40);
    this.shapes.ring(this.player.x, this.player.z, 0.4, 3, this.tmpColor.set('#5fc8ff'), 0.5, 3);
    this.ui.showLevelUp(t('levelup.title', { n: this.level - this.pendingLevels + 1 }), this.pendingLevels > 1 ? t('levelup.waiting', { n: this.pendingLevels }) : t('levelup.choose'), this.options, this.rerolls, this.banishes, false);
  }

  private rollOptions(locked?: UpgradeOption[] | null): UpgradeOption[] {
    const pool: Array<{ o: UpgradeOption; w: number }> = [];
    const owned = this.spells.owned;
    const ownedPassives = new Set(this.passives.map((p) => p.def.id));
    for (const s of owned) {
      if (s.evolved || s.level >= SPELL_MAX_LEVEL || this.banishedIds.has(s.def.id)) continue;
      const evo = s.level + 1 === SPELL_MAX_LEVEL ? passiveById(s.def.evolution.passive) : null;
      pool.push({
        w: 1.3,
        o: {
          kind: 'spell', id: s.def.id, name: spellName(s.def), icon: spellIcon(s.def.id, s.def.icon), color: s.def.color,
          tag: s.level + 1 === SPELL_MAX_LEVEL ? t('card.levelMax', { a: s.level }) : t('card.level', { a: s.level, b: s.level + 1 }), tagClass: '',
          desc: formatUpgrade(s.def.upgrades[s.level - 1]),
          hint: evo ? `${t('card.evolvesWith', { name: passiveText(evo.id).name })}${ownedPassives.has(evo.id) ? ' ✓' : ''}` : undefined,
        },
      });
    }
    for (const p of this.passives) {
      if (p.level >= p.def.maxLevel || this.banishedIds.has(p.def.id)) continue;
      pool.push({ w: 1.1, o: { kind: 'passive', id: p.def.id, name: passiveText(p.def.id).name, icon: passiveIcon(p.def.id, p.def.icon), color: p.def.color, tag: t('card.level', { a: p.level, b: p.level + 1 }), tagClass: '', desc: passiveText(p.def.id).desc } });
    }
    if (owned.length < MAX_SPELLS) {
      for (const def of SPELLS) {
        if (owned.some((s) => s.def.id === def.id) || this.banishedIds.has(def.id)) continue;
        const evo = passiveById(def.evolution.passive);
        pool.push({ w: 1, o: { kind: 'spell', id: def.id, name: spellName(def), icon: spellIcon(def.id, def.icon), color: def.color, tag: t('card.newSpell'), tagClass: 'new', desc: spellDesc(def), hint: ownedPassives.has(evo.id) ? `${t('card.evolvesWith', { name: passiveText(evo.id).name })} ✓` : undefined } });
      }
    }
    if (this.passives.length < MAX_PASSIVES) {
      for (const def of PASSIVES) {
        if (ownedPassives.has(def.id) || this.banishedIds.has(def.id)) continue;
        const evolves = owned.find((s) => s.def.evolution.passive === def.id && !s.evolved);
        pool.push({ w: evolves ? 1.2 : 0.9, o: { kind: 'passive', id: def.id, name: passiveText(def.id).name, icon: passiveIcon(def.id, def.icon), color: def.color, tag: t('card.new'), tagClass: 'new', desc: passiveText(def.id).desc, hint: evolves ? t('card.evolves', { name: spellName(evolves.def) }) : undefined } });
      }
    }
    if (pool.length === 0) {
      return [
        { kind: 'gold', id: 'gold', name: t('card.goldCache'), icon: passiveIcon('fortune', '◉'), color: '#ffd24d', tag: t('card.bonus'), tagClass: '', desc: t('card.goldCacheDesc') },
        { kind: 'heal', id: 'heal', name: t('card.draught'), icon: passiveIcon('heart', '♥︎'), color: '#ff5d8f', tag: t('card.bonus'), tagClass: '', desc: t('card.draughtDesc') },
      ];
    }
    const n = this.perks.has('scholar_crown') || this.rng() < 0.12 + this.mods.luck ? 4 : 3;
    const picked: UpgradeOption[] = [];
    if (locked && locked.length > 0) {
      for (const prev of locked) {
        const idx = pool.findIndex((c) => c.o.kind === prev.kind && c.o.id === prev.id);
        if (idx >= 0) picked.push(pool.splice(idx, 1)[0].o);
      }
    }
    while (picked.length < n && pool.length) {
      const total = pool.reduce((s, c) => s + c.w, 0);
      let roll = this.rng() * total;
      let idx = 0;
      for (; idx < pool.length - 1; idx += 1) {
        roll -= pool[idx].w;
        if (roll <= 0) break;
      }
      picked.push(pool.splice(idx, 1)[0].o);
    }
    return picked;
  }

  private pick(index: number): void {
    if (this.state !== 'levelup') return;
    const o = this.options[index];
    if (!o) return;
    this.applyOption(o);
    if (this.lockedOptions) {
      const remaining = this.options.filter((_, idx) => idx !== index);
      this.lockedOptions = remaining.length > 0 ? remaining : null;
    }
    this.audio.ui();
    this.pendingLevels -= 1;
    if (this.pendingLevels > 0) {
      this.openLevelUp();
      return;
    }
    this.ui.hideLevelUp();
    this.state = 'playing';
  }

  private applyOption(o: UpgradeOption): void {
    if (o.kind === 'spell') {
      const s = this.spells.owned.find((sp) => sp.def.id === o.id);
      if (s) {
        s.level = Math.min(SPELL_MAX_LEVEL, s.level + 1);
        this.spells.refresh(this.mods);
      } else this.spells.add(spellById(o.id), this.mods);
      this.refreshSlots();
    } else if (o.kind === 'passive') {
      const p = this.passives.find((pp) => pp.def.id === o.id);
      if (p) p.level += 1;
      else this.passives.push({ def: passiveById(o.id), level: 1 });
      this.recalc();
    } else if (o.kind === 'gold') {
      this.goldRun += 25;
    }
    else this.player.heal(40);
  }

  private reroll(): void {
    if (this.state !== 'levelup' || this.rerolls <= 0) return;
    this.rerolls -= 1;
    this.lockedOptions = null;
    this.options = this.rollOptions();
    this.audio.ui();
    this.ui.showLevelUp(t('levelup.title', { n: this.level - this.pendingLevels + 1 }), t('levelup.rerolled'), this.options, this.rerolls, this.banishes, false);
  }

  private banish(): void {
    if (this.state !== 'levelup' || this.banishes <= 0 || this.options.length <= 1) return;
    const removed = this.options.shift()!;
    this.banishes -= 1;
    this.banishedIds.add(removed.id);
    this.lockedOptions = null;
    this.audio.ui();
    this.ui.showLevelUp(t('levelup.title', { n: this.level - this.pendingLevels + 1 }), t('levelup.banished', { name: removed.name }), this.options, this.rerolls, this.banishes, false);
  }

  private toggleLock(): void {
    if (this.state !== 'levelup') return;
    this.lockedOptions = this.lockedOptions ? null : [...this.options];
    this.audio.ui();
    this.ui.showLevelUp(t('levelup.title', { n: this.level - this.pendingLevels + 1 }), t(this.lockedOptions ? 'levelup.locked' : 'levelup.choose'), this.options, this.rerolls, this.banishes, this.lockedOptions !== null);
  }

  private openChest(): void {
    this.state = 'chest';
    this.pendingChests -= 1;
    this.guard.recordEvent('chest', this.level);
    this.input.clear();
    this.audio.chest();
    vibrate([30, 40, 60]);
    this.ui.flash(0.4, 300);
    const ownedPassives = new Set(this.passives.map((p) => p.def.id));
    const evo = this.spells.owned.find((s) => !s.evolved && s.level >= SPELL_MAX_LEVEL && ownedPassives.has(s.def.evolution.passive));
    if (evo) {
      this.save.evolutions[evo.def.id] = true;
      this.spells.evolve(evo, this.mods);
      this.refreshSlots();
      this.banner(t('banner.evolution'), spellName(evo.def, true), '#ffd24d');
      this.ui.showChest(t('chest.evolution'), t('chest.evolvedSub', { name: spellName(evo.def) }), [
        { kind: 'spell', id: evo.def.id, name: spellName(evo.def, true), icon: spellIcon(evo.def.id, evo.def.icon), color: evo.def.evolution.color, tag: t('card.evolved'), tagClass: 'evo', desc: spellDesc(evo.def, true) },
      ]);
      return;
    }
    const results: UpgradeOption[] = [];
    const count = this.rng() < 0.25 ? 3 : 1;
    for (let i = 0; i < count; i += 1) {
      const opts = this.rollOptions().filter((o) => o.kind === 'spell' || o.kind === 'passive');
      const upgrades = opts.filter((o) => o.tagClass !== 'new');
      const choice = upgrades[0] ?? opts[0];
      if (!choice) break;
      this.applyOption(choice);
      results.push(choice);
    }
    const gold = goldDrop(25 + this.rng() * 35, this.stageIndex, this.mods.greed);
    this.goldRun += gold;
    results.push({ kind: 'gold', id: 'gold', name: t('card.gold', { n: gold }), icon: passiveIcon('fortune', '◉'), color: '#ffd24d', tag: t('card.treasure'), tagClass: '', desc: t('card.goldDesc') });
    this.ui.showChest(t('chest.treasure'), results.length > 2 ? t('chest.bountiful') : t('chest.holds'), results);
  }

  private closeChest(): void {
    if (this.state !== 'chest') return;
    this.ui.hideLevelUp();
    this.state = 'playing';
  }

  private refreshSlots(): void {
    const ownedPassives = new Set(this.passives.map((p) => p.def.id));
    const spells: SlotView[] = this.spells.owned.map((s) => ({
      icon: spellIcon(s.def.id, s.def.icon),
      color: s.evolved ? s.def.evolution.color : s.def.color,
      level: s.evolved ? '★' : s.level >= SPELL_MAX_LEVEL ? t('hud.max') : num(s.level),
      evolved: s.evolved,
      ready: !s.evolved && s.level >= SPELL_MAX_LEVEL && ownedPassives.has(s.def.evolution.passive),
      title: s.evolved ? spellName(s.def, true) : `${spellName(s.def)} · ${t('card.evolvesWith', { name: passiveText(s.def.evolution.passive).name })}`,
    }));
    const passives: SlotView[] = this.passives.map((p) => ({ icon: passiveIcon(p.def.id, p.def.icon), color: p.def.color, level: p.level >= p.def.maxLevel ? t('hud.max') : num(p.level), title: `${passiveText(p.def.id).name}: ${passiveText(p.def.id).desc}` }));
    this.ui.setSlots(spells, passives);
  }

  // Kills ahead (+) or behind (-) this device's best run on this realm and tier at the same moment.
  private paceDelta(): number | null {
    const best = this.paceRef ? paceAt(this.paceRef, this.time) : null;
    return best === null ? null : this.kills - Math.round(best);
  }

  private updateHud(): void {
    if (this.state === 'menu') return;
    const boss = this.enemies.boss;
    this.ui.updateHud({
      time: this.time,
      wave: this.enemies.waves.wave,
      progress: this.time / this.stage.duration,
      kills: this.kills,
      pace: this.paceDelta(),
      gold: this.goldRun,
      level: this.level,
      xp: this.xp,
      xpNext: xpToNext(this.level),
      hp: this.player.hp,
      maxHp: this.player.maxHp,
      dash: 1 - this.player.dashCd / DASH_COOLDOWN,
      boss: boss ? { name: this.bossName, ratio: boss.hp / this.bossMaxHp } : null,
    });
  }

  // ======================= Menus =======================

  private onAction(action: string): void {
    this.audio.ui();
    if (PLAY_ACTIONS.has(action) && !this.cloud.account) {
      void this.requireAccount(action);
      return;
    }
    switch (action) {
      case 'play':
        this.ui.renderSelect(this.save);
        this.ui.show('select');
        break;
      case 'shop':
        if (this.state === 'results') this.toMenu();
        this.ui.renderShop(this.save);
        this.ui.show('shop');
        break;
      case 'armory':
        this.armoryTab = 'equipment';
        this.renderArmory();
        this.ui.show('armory');
        break;
      case 'armory-tab:equipment':
        this.armoryTab = 'equipment';
        this.renderArmory();
        break;
      case 'armory-tab:chests':
        this.armoryTab = 'chests';
        this.renderArmory();
        break;
      case 'armory-tab:market':
        this.armoryTab = 'market';
        this.renderArmory();
        break;
      case 'item-close':
        this.ui.hideItem();
        break;
      case 'item-equip':
        this.ui.hideItem();
        this.equip(this.selectedItem);
        break;
      case 'item-salvage':
        this.ui.hideItem();
        this.salvage(this.selectedItem);
        break;
      case 'item-enhance':
        this.enhance(this.selectedItem);
        break;
      case 'item-reforge':
        this.reforge(this.selectedItem);
        break;
      case 'item-fuse':
        this.fuse(this.selectedItem);
        break;
      case 'salvage-commons':
        this.salvageCommons();
        break;
      case 'gear-equip':
        this.ui.hideGearReveal();
        if (this.revealedItems[0]) this.equip(this.revealedItems[0]);
        break;
      case 'gear-keep':
      case 'gear-keep-all':
        this.ui.hideGearReveal();
        break;
      case 'gear-salvage':
        this.ui.hideGearReveal();
        if (this.revealedItems[0]) this.salvage(this.revealedItems[0]);
        break;
      case 'gear-salvage-commons':
        this.ui.hideGearReveal();
        this.salvageCommons();
        break;
      case 'settings':
        this.ui.renderSettings(this.save.settings);
        this.ui.show('settings');
        break;
      case 'howto':
        this.ui.show('howto');
        break;
      case 'back':
        this.ui.renderTitle(this.save);
        this.ui.show('title');
        break;
      case 'start':
        void this.beginRun(false);
        break;
      case 'daily-trial':
        void this.beginRun(true);
        break;
      case 'pause':
        this.pause();
        break;
      case 'resume':
        this.resume();
        break;
      case 'quit':
        this.ui.hidePause();
        this.endRun(false);
        break;
      case 'retry':
        void this.beginRun(this.dailyActive);
        break;
      case 'endless':
        this.enterEndless();
        break;
      case 'next':
        this.save.stage = Math.min(STAGES.length - 1, this.stageIndex + 1);
        this.persist();
        void this.beginRun(false);
        break;
      case 'menu':
        this.toMenu();
        break;
      case 'fullscreen':
        toggleFullscreen();
        break;
      case 'install':
        void promptInstall().then((prompted) => (prompted ? this.ui.syncInstall() : this.ui.showInstallHelp(true)));
        break;
      case 'install-close':
        this.ui.showInstallHelp(false);
        break;
      case 'lang':
        this.setLanguage(getLang() === 'fa' ? 'en' : 'fa');
        break;
      case 'chest-close':
        this.closeChest();
        break;
      case 'account':
        this.playAfterSignIn = false;
        this.ui.showAccount(this.cloud.account);
        break;
      case 'account-close':
        this.playAfterSignIn = false;
        this.tgRun += 1;
        this.ui.hideAccount();
        break;
      case 'telegram':
        void this.telegramSignIn(false);
        break;
      case 'telegram-link':
        void this.telegramSignIn(true);
        break;
      case 'telegram-cancel':
        this.tgRun += 1;
        this.ui.showAccount(this.cloud.account);
        break;
      case 'leaderboard':
        this.ui.show('leaderboard');
        void this.loadLeaderboard('all');
        break;
    }
  }

  // ======================= Account =======================

  private persist(): void {
    writeSave(this.save);
    this.cloud.schedulePush();
  }

  // The account's progress as the server just sent it: it is authoritative, so it overwrites what this device had.
  private applyProgress(p: Progress): void {
    Object.assign(this.save, p);
    writeSave(this.save);
    this.ui.localize(this.save);
  }

  // Playing needs an account. Waits for the session restore first, so a returning player is never asked again.
  private async requireAccount(action: string): Promise<void> {
    await this.restored;
    if (this.cloud.account) {
      this.onAction(action);
      return;
    }
    this.playAfterSignIn = true;
    this.ui.showAccount(null);
  }

  private async auth(mode: AuthMode, email: string, password: string, name: string): Promise<void> {
    this.ui.showAccount(null, { busy: true, email, name });
    const error = await this.cloud.signIn(mode, email, password, name);
    if (error) this.ui.showAccount(null, { error, email, name });
    else this.afterSignIn();
  }

  // Shows the bot link, then polls every 2 s until the player confirms in Telegram, the code expires, or they leave.
  // link: connect Telegram to the signed-in player instead of signing in.
  private async telegramSignIn(link: boolean): Promise<void> {
    const run = ++this.tgRun;
    this.ui.showAccount(this.cloud.account, { busy: true });
    const start = await this.cloud.telegramStart(link);
    if (run !== this.tgRun) return;
    if (typeof start === 'string') {
      this.ui.showAccount(this.cloud.account, { error: start });
      return;
    }
    this.ui.showTelegramWait(start.url, start.match);
    while (run === this.tgRun) {
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
      if (run !== this.tgRun || !this.ui.accountOpen) return;
      const result = await this.cloud.telegramPoll(start.code);
      if (run !== this.tgRun) return;
      if (result === 'done') {
        this.afterSignIn();
        return;
      }
      if (result !== 'pending' && result !== 'network') {
        this.ui.showAccount(this.cloud.account, { error: result });
        return;
      }
    }
  }

  private afterSignIn(): void {
    if (this.playAfterSignIn) {
      this.playAfterSignIn = false;
      this.ui.hideAccount();
      if (this.state === 'results') this.toMenu();
      this.onAction('play');
    } else this.ui.showAccount(this.cloud.account);
  }

  private async loadLeaderboard(stage: string): Promise<void> {
    this.ui.renderLeaderboard(stage, 'loading', !!this.cloud.account);
    const data = await this.cloud.leaderboard(stage);
    this.ui.renderLeaderboard(stage, data ?? 'error', !!this.cloud.account);
  }

  private pause(): void {
    if (this.state !== 'playing') return;
    this.state = 'paused';
    this.input.clear();
    const rows = [...this.spells.owned].sort((a, b) => b.damage - a.damage);
    const m = this.mods;
    const pct = (v: number) => percent(Math.round(v * 100), true);
    const stats = [
      [t('stat.damage'), pct(m.might)], [t('stat.cooldown'), pct(-m.haste)], [t('stat.area'), pct(m.area)], [t('stat.projectiles'), num(m.amount, { signDisplay: 'always' })],
      [t('stat.speed'), pct(m.moveSpeed)], [t('stat.armor'), num(m.armor, { maximumFractionDigits: 1 })], [t('stat.regen'), t('stat.regenValue', { v: num(m.regen, { minimumFractionDigits: 1, maximumFractionDigits: 2 }) })], [t('stat.crit'), percent(Math.round((0.05 + m.crit) * 100))],
    ];
    const passives = this.passives.map((p) => `<span class="ticon" style="--c:${p.def.color}">${passiveIcon(p.def.id, p.def.icon)}</span> ${escapeHtml(passiveText(p.def.id).name)} ${num(p.level)}`).join(' · ') || t('pause.none');
    const sub = t('pause.sub', { stage: stageText(this.stage.id).name, time: formatTime(this.time), n: this.level });
    this.ui.showPause(`
      <p class="sub">${escapeHtml(sub)}</p>
      ${this.damageTable(rows, Math.max(1, rows[0]?.damage ?? 1))}
      <p class="sub passive-line">${passives}</p>
      <div class="statlist">${stats.map(([k, v]) => `<div><span>${k}</span><b>${v}</b></div>`).join('')}</div>`);
  }

  private resume(): void {
    if (this.state !== 'paused') return;
    this.ui.hidePause();
    this.state = 'playing';
  }

  private readonly onKey = (event: KeyboardEvent) => {
    if (event.code === 'Escape' && this.ui.accountOpen) {
      this.onAction('account-close'); // also cancels a Telegram sign-in in progress
      return;
    }
    if (event.code === 'Escape' && this.ui.itemOpen) {
      this.ui.hideItem();
      return;
    }
    if (event.code === 'Escape' && this.ui.gearOpen) {
      this.ui.hideGearReveal(); // the item is already stored, so this is Keep
      return;
    }
    if ((event.target as HTMLElement | null)?.closest?.('input, textarea, select')) return;
    if (event.code === 'Escape' || event.code === 'KeyP') {
      if (this.state === 'playing') this.pause();
      else if (this.state === 'paused') this.resume();
    }
  };

  private readonly onVisibility = () => {
    if (document.hidden && this.state === 'playing') this.pause();
  };

  private selectCharacter(id: string): void {
    this.save.character = id;
    this.persist();
    this.ui.renderSelect(this.save);
    this.player.setCharacter(this.currentCharacter());
    this.audio.ui();
  }

  private selectStage(index: number): void {
    if (index === this.save.stage) return;
    this.save.stage = index;
    this.persist();
    this.ui.renderSelect(this.save);
    this.loadStage(index);
    this.audio.playMusic(this.stage.music);
    this.audio.ui();
  }

  private selectStormTier(tier: number): void {
    if (tier === this.save.stormTier) return;
    this.save.stormTier = tier;
    this.persist();
    this.ui.renderSelect(this.save);
    this.audio.ui();
  }

  private enterEndless(): void {
    if (this.state !== 'results') return;
    this.endless = true;
    this.bossDefeatedAt = -1;
    this.ui.hideResults();
    this.state = 'playing';
    this.banner(t('banner.endless'), t('banner.endlessSub'), '#c27bff');
  }

  // Every purchase is one request: the server prices it, moves the gold and answers with the progress it left, and the
  // screens are drawn from that answer. Nothing is spent or granted on this device.
  private act(action: Action, done?: (res: ActResult) => void): void {
    if (this.acting) return; // one request at a time: a double click cannot buy twice
    this.acting = true;
    void this.cloud.act(action).then((res) => {
      this.acting = false;
      if (typeof res === 'string') {
        if (res === 'network' || res === 'signed_out') this.ui.toast(t('shop.offline'));
        return;
      }
      done?.(res);
    });
  }

  private buy(id: string): void {
    this.act({ kind: 'shop', id }, () => {
      this.ui.renderShop(this.save);
      this.audio.pickup();
    });
  }

  private claimEnemyBounty(enemyId: string): void {
    this.act({ kind: 'bounty', enemy: enemyId }, () => {
      this.ui.renderShop(this.save);
      this.audio.pickup();
    });
  }

  private renderArmory(): void {
    const c = this.currentCharacter();
    const mods = this.permanentMods();
    const portrait = heroPortrait(this.gfx.renderer, c.robe, c.trim);
    this.ui.renderArmory(this.save, {
      tab: this.armoryTab,
      portrait,
      damage: Math.round(mods.might * 100),
      maxHp: Math.round(BASE_HP + mods.maxHp),
    });
  }

  private openItem(itemId: string): void {
    const item = this.save.gear.items.find((it) => it.id === itemId);
    if (!item) return;
    this.selectedItem = item.id;
    const worn = Object.values(this.save.gear.equipped).includes(item.id);
    this.ui.showItem(item, worn, this.save.gold, this.save.gear);
    this.audio.ui();
  }

  private buyChest(id: string, count: 1 | 10 = 1): void {
    this.act({ kind: 'chest', id, count }, (res) => this.revealBought(res));
  }

  private buyGear(base: string, rarity: number): void {
    this.act({ kind: 'buy', base, rarity }, (res) => this.revealBought(res));
  }

  private revealBought(res: ActResult): void {
    if (!res.items?.length) return;
    this.revealedItems = res.items.map((it) => it.id);
    this.renderArmory();
    this.ui.showReveal(res.items);
    this.audio.chest();
  }

  private equip(itemId: string): void {
    this.act({ kind: 'equip', item: itemId }, () => {
      this.renderArmory();
      this.audio.ui();
    });
  }

  private enhance(itemId: string): void {
    this.act({ kind: 'enhance', item: itemId }, () => {
      this.renderArmory();
      this.showItemAgain(itemId);
      this.audio.levelUp();
    });
  }

  private reforge(itemId: string): void {
    this.act({ kind: 'reforge', item: itemId }, () => {
      this.renderArmory();
      this.showItemAgain(itemId);
      this.audio.levelUp();
    });
  }

  private fuse(itemId: string): void {
    this.act({ kind: 'fuse', item: itemId }, (res) => {
      const created = res.items?.[0];
      if (!created) return;
      this.ui.hideItem();
      this.revealedItems = [created.id];
      this.renderArmory();
      this.ui.showReveal([created]);
      this.audio.chest();
    });
  }

  private salvage(itemId: string): void {
    this.act({ kind: 'salvage', item: itemId }, () => {
      this.renderArmory();
      this.audio.pickup();
    });
  }

  private salvageCommons(): void {
    this.act({ kind: 'salvage-commons' }, () => {
      this.renderArmory();
      this.audio.pickup();
    });
  }

  // Keeps the item modal open on the item it was showing, with the server's new numbers.
  private showItemAgain(itemId: string): void {
    const item = this.save.gear.items.find((it) => it.id === itemId);
    if (!item) return;
    const worn = Object.values(this.save.gear.equipped).includes(item.id);
    this.ui.showItem(item, worn, this.save.gold, this.save.gear);
  }

  private setLanguage(lang: Lang): void {
    setLang(lang);
    this.save.settings.lang = lang;
    applyStatic();
    writeSave(this.save);
    this.ui.localize(this.save);
    this.refreshSlots();
  }

  private applySettings(s: Settings, persist = true): void {
    this.save.settings = s;
    this.gfx.setQuality(s.graphicsMode);
    this.applyGraphics();
    this.audio.setVolumes(s.sfx, s.music);
    this.overlay.numbersEnabled = s.numbers;
    this.input.configure({ mode: s.joystick, leftHanded: s.leftHanded });
    setVibration(s.haptics);
    if (persist) writeSave(this.save);
  }

  // The scene's side of a graphics tier (the renderer's side is in GameRenderer).
  private applyGraphics(): void {
    this.env.sun.castShadow = this.gfx.shadowsOn;
    this.env.setShadowSize(this.gfx.shadowSize);
    this.particles.density = this.gfx.particleDensity;
    this.player.light.visible = this.gfx.tier !== 'low';
    document.body.classList.toggle('gfx-low', this.gfx.tier === 'low'); // CSS drops full-screen decorative layers
    this.gfx.warm();
  }

  private applyTuning(): void {
    this.gfx.setBloomStrength(this.tuning.bloom);
    this.gfx.renderer.toneMappingExposure = this.tuning.exposure;
  }

  // ======================= Test hooks & diagnostics =======================

  private installTestHooks(): void {
    if (!import.meta.env.DEV) return; // test hooks are a cheat vector in production
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (!['menu', 'active-play', 'levelup', 'boss', 'results'].includes(name)) throw new Error(`Unknown test state: ${name}`);
        if (name === 'menu') this.toMenu();
        else {
          this.startRun();
          if (name === 'levelup') {
            this.pendingLevels = 1;
            this.openLevelUp();
          } else if (name === 'boss') {
            this.time = this.stage.duration;
            this.simulate(0.016);
          } else if (name === 'results') this.endRun(false);
        }
        this.render();
        this.publishDiagnostics();
        return { state: name };
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedFx = enabled;
        this.render();
      },
      hideDebugUi: (hidden: boolean) => this.debug?.setHidden(hidden),
      grant: (id: string) => {
        this.guard.markTainted('debug_tools');
        if (SPELLS.some((s) => s.id === id)) {
          const s = this.spells.owned.find((sp) => sp.def.id === id);
          if (s) s.level = Math.min(SPELL_MAX_LEVEL, s.level + 1);
          else if (this.spells.owned.length < MAX_SPELLS) this.spells.add(spellById(id), this.mods);
          this.spells.refresh(this.mods);
          this.refreshSlots();
        } else if (PASSIVES.some((p) => p.id === id)) {
          this.applyOption({ kind: 'passive', id, name: '', icon: '', color: '', tag: '', tagClass: '', desc: '' });
        } else if (id === 'chest') this.pendingChests += 1;
        else throw new Error(`Unknown grant id ${id}`);
      },
      setStage: (index: number) => {
        if (!STAGES[index]) throw new Error(`Unknown stage ${index}`);
        this.save.stage = index;
      },
      setTime: (seconds: number) => {
        this.guard.markTainted('debug_tools');
        this.time = seconds;
      },
      godMode: (enabled: boolean) => {
        if (enabled) this.guard.markTainted('god_mode');
        this.tuning.god = enabled;
      },
      crowd: () => this.enemies.crowdStats(),
      simulate: (seconds: number, autopilot = true) => {
        const dt = 1 / 30;
        const started = performance.now();
        for (let i = 0; i < Math.ceil(seconds / dt); i += 1) {
          if (this.state === 'levelup') this.pick(Math.floor(this.rng() * this.options.length));
          else if (this.state === 'chest') this.closeChest();
          if (this.state !== 'playing') break;
          if (autopilot) {
            // Chase the nearest gem; otherwise circle to kite the horde.
            const a = this.time * 0.3;
            const g = this.pickups.nearest(this.player.x, this.player.z, 14);
            const dx = g ? g.x - this.player.x : Math.cos(a);
            const dz = g ? g.z - this.player.z : Math.sin(a);
            const len = Math.hypot(dx, dz) || 1;
            this.botMove = { x: dx / len, y: dz / len };
          }
          this.time += dt;
          this.simulate(dt);
        }
        this.botMove = null;
        this.followCamera(1);
        this.render();
        this.publishDiagnostics();
        return {
          state: this.state, time: Math.round(this.time), level: this.level, kills: this.kills, hp: Math.round(this.player.hp),
          enemies: this.enemies.hostile, spells: this.spells.owned.map((sp) => `${sp.def.id}:${sp.evolved ? 'evo' : sp.level}`),
          passives: this.passives.map((pp) => `${pp.def.id}:${pp.level}`), ms: Math.round(performance.now() - started),
        };
      },
    };
  }

  private publishDiagnostics(): void {
    const info = this.gfx.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.time,
      score: this.kills,
      targetScore: 0,
      complete: this.bossDefeatedAt >= 0,
      state: this.state,
      stage: this.stage.id,
      level: this.level,
      hp: this.player.hp,
      enemies: this.enemies.hostile,
      bullets: this.enemies.bulletCount,
      particles: this.particles.active,
      gems: this.pickups.gemCount,
      spells: this.spells.owned.map((s) => `${s.def.id}:${s.evolved ? 'evo' : s.level}`),
      mods: { ...this.mods },
      perks: [...this.perks],
      integrity: { tainted: this.guard.tainted, reasons: [...this.guard.reasons] },
      boss: this.enemies.boss ? this.enemies.boss.hp : null,
      dashReady: this.player.dashCd <= 0,
      graphics: this.gfx.graphics,
      touch: document.body.classList.contains('touch'),
      player: {
        position: { x: this.player.x, y: 0, z: this.player.z },
        speed: Math.hypot(this.player.vx, this.player.vz),
      },
      renderer: { calls: info.render.calls, triangles: info.render.triangles, geometries: info.memory.geometries, textures: info.memory.textures },
      canvas: {
        clientWidth: this.gfx.cssSize.width,
        clientHeight: this.gfx.cssSize.height,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: this.gfx.renderer.getPixelRatio(),
      },
    };
  }

  private el<T extends HTMLElement = HTMLElement>(sel: string): T {
    const el = document.querySelector<T>(sel);
    if (!el) throw new Error(`Missing element ${sel}`);
    return el;
  }
}
